import type { PillTone } from '../../components/primitives';
import { BRIEF_LIMITS } from '../../../shared/morningBrief';
import type { EmailLogStatus, HubAutomationKey } from './types';

// §1 inventory metadata not carried by the API payload (titles/cadence/
// audience are presentation, not settings) — kept in one place so the card
// and the template-editor modal title agree.
export const AUTOMATION_META: Record<
  HubAutomationKey,
  { title: string; cadence: string; audience: AutomationAudience }
> = {
  invoice_reminder_ladder: { title: 'Invoice reminder ladder', cadence: 'Nightly · 03:00', audience: 'client' },
  shoot_day_nudge: { title: 'Shoot-day nudge', cadence: 'Nightly · 03:00', audience: 'client' },
  testimonial_ask: { title: 'Testimonial ask', cadence: 'Nightly · 03:00', audience: 'client' },
  waiting_on_client_nudge: {
    title: 'Waiting-on-client nudge',
    cadence: 'Nightly · 03:00 + send-now',
    audience: 'client',
  },
  stale_proposal_digest: { title: 'Stale-proposal digest', cadence: 'Weekly', audience: 'you' },
  weekly_digest: { title: 'Weekly digest', cadence: 'Weekly · your chosen day', audience: 'you' },
  morning_brief: { title: 'Morning brief', cadence: 'Weekdays · 05:00 Melbourne · Claude routine', audience: 'overview' },
  proposal_signed_receipt: { title: 'Proposal-signed receipt', cadence: 'Event-triggered', audience: 'client' },
  lead_resurface: { title: 'Lead resurface', cadence: 'Nightly · 03:00', audience: 'none' },
  meta_ads_sync: { title: 'Meta Ads sync', cadence: 'Nightly · 03:00', audience: 'none' },
  invoice_send: { title: 'Send invoice', cadence: 'Manual — triggered by you', audience: 'client' },
};

/** 'overview' writes to the app, not an inbox: no template, no send log,
    and the email master switch does not pause it. */
export type AutomationAudience = 'client' | 'you' | 'none' | 'overview';

/** Audiences with no email behind them (no Template / Recent sends links). */
export function sendsNoEmail(audience: AutomationAudience): boolean {
  return audience === 'none' || audience === 'overview';
}

export interface IntegerKnobMeta { type?: 'integer'; label: string; unit: string; min: number; max: number }
export interface TextKnobMeta { type: 'text'; label: string; max: number; placeholder: string; multiline: boolean }
export type KnobMeta = IntegerKnobMeta | TextKnobMeta;

// Mirrors worker/routes/automations.ts's KNOB_RULES — the server is the
// source of truth and re-validates on save; these just drive the input's
// limits and a human label/unit.
export const KNOB_META: Record<string, KnobMeta> = {
  weekday: { label: 'Send day', unit: '', min: 0, max: 6 },
  quiet_days: { label: 'Quiet days', unit: 'days', min: 1, max: 30 },
  rearm_days: { label: 'Re-arm', unit: 'days', min: 1, max: 90 },
  cap: { label: 'Send cap', unit: 'emails', min: 1, max: 3 },
  reminder_delay_days: { label: 'Reminder delay', unit: 'days after delivery', min: 1, max: 30 },
  age_days: { label: 'Age threshold', unit: 'days', min: 1, max: 90 },
  focus_notes: {
    type: 'text',
    label: 'Your focus (included in every brief)',
    max: BRIEF_LIMITS.focus_notes,
    placeholder: 'e.g. "Retainers first this quarter. Protect Fridays for editing."',
    multiline: true,
  },
  routine_url: {
    type: 'text', label: 'Routine URL', max: BRIEF_LIMITS.routine_url,
    placeholder: 'https://claude.ai/code/routines/…', multiline: false,
  },
};

export function isTextKnob(meta: KnobMeta | undefined): meta is TextKnobMeta {
  return meta?.type === 'text';
}

export const STATUS_LABEL: Record<EmailLogStatus, string> = {
  sent: 'Sent',
  failed: 'Failed',
  skipped_pref: 'Skipped',
  skipped_disabled: 'Skipped',
};
export const STATUS_CLASS: Record<EmailLogStatus, string> = {
  sent: 'sent',
  failed: 'failed',
  skipped_pref: 'skipped',
  skipped_disabled: 'skipped',
};
/** Same three states as .atm-hist-status, as Pill tones for the mobile sheet. */
export const STATUS_TONE: Record<EmailLogStatus, PillTone> = {
  sent: 'ok',
  failed: 'danger',
  skipped_pref: undefined,
  skipped_disabled: undefined,
};
